import React, { useEffect, useMemo, useRef } from 'react';
import ReactDOM from 'react-dom';
import { milestoneColor } from '../lib/milestoneColor';

// Helpers
const parseDate = (s) => s ? new Date(s + 'T00:00:00') : null;
const fmtDateShort = (s) => {
  const d = parseDate(s);
  if (!d) return '';
  return `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}/${String(d.getFullYear()).slice(-2)}`;
};
const fmtRangeHeader = (s) => {
  const d = parseDate(s);
  if (!d) return '';
  return d.toLocaleString('default', { month: 'short', year: 'numeric' });
};
const daysBetween = (a, b) => Math.round((b - a) / (1000 * 60 * 60 * 24));

const STATUS_COLORS = {
  complete: '#16a34a',
  completed: '#16a34a',
  'in-progress': '#2563eb',
  'in progress': '#2563eb',
  'not-started': '#9ca3af',
  'not started': '#9ca3af',
};
const getStatusColor = (task) => {
  const progress = task.percent_complete || task.progress || 0;
  if (progress >= 100) return STATUS_COLORS.complete;
  if (progress > 0) return STATUS_COLORS['in-progress'];
  return STATUS_COLORS['not started'];
};

const PrintScheduleView = ({
  projectName,
  projectNumber,
  rangeLabel,
  from,
  to,
  tasks,
  sections,
  groupedTasks,
  onClose,
}) => {
  const printRef = useRef(null);

  const rangeStart = useMemo(() => parseDate(from), [from]);
  const rangeEnd = useMemo(() => parseDate(to), [to]);
  const totalDays = useMemo(
    () => (rangeStart && rangeEnd ? Math.max(1, daysBetween(rangeStart, rangeEnd) + 1) : 0),
    [rangeStart, rangeEnd]
  );

  // Tasks that overlap the chosen range (any portion of the bar within range)
  const filteredGrouped = useMemo(() => {
    const out = {};
    sections.forEach(section => {
      const list = (groupedTasks[section] || []).filter(task => {
        const s = parseDate(task.start_date);
        const e = parseDate(task.finish_date) || s;
        if (!s) return false;
        return e >= rangeStart && s <= rangeEnd;
      });
      if (list.length) out[section] = list;
    });
    return out;
  }, [sections, groupedTasks, rangeStart, rangeEnd]);

  const visibleSections = sections.filter(s => (filteredGrouped[s] || []).length > 0);

  // Auto-pick a band granularity for the time axis
  const band = useMemo(() => {
    if (totalDays <= 35) return 'day';
    if (totalDays <= 100) return 'week';
    if (totalDays <= 400) return 'month';
    return 'quarter';
  }, [totalDays]);

  // Build time-axis "ticks" — pairs of {start, label} for the chosen band
  const ticks = useMemo(() => {
    if (!rangeStart || !rangeEnd) return [];
    const out = [];
    const cur = new Date(rangeStart);
    if (band === 'day') {
      while (cur <= rangeEnd) {
        out.push({ date: new Date(cur), label: String(cur.getDate()) });
        cur.setDate(cur.getDate() + 1);
      }
    } else if (band === 'week') {
      // Snap to Monday
      const dow = cur.getDay();
      if (dow !== 1) cur.setDate(cur.getDate() - ((dow + 6) % 7));
      while (cur <= rangeEnd) {
        out.push({
          date: new Date(cur),
          label: `${String(cur.getMonth() + 1).padStart(2, '0')}/${String(cur.getDate()).padStart(2, '0')}`,
        });
        cur.setDate(cur.getDate() + 7);
      }
    } else if (band === 'month') {
      cur.setDate(1);
      while (cur <= rangeEnd) {
        out.push({
          date: new Date(cur),
          label: cur.toLocaleString('default', { month: 'short', year: '2-digit' }),
        });
        cur.setMonth(cur.getMonth() + 1);
      }
    } else {
      cur.setMonth(Math.floor(cur.getMonth() / 3) * 3);
      cur.setDate(1);
      while (cur <= rangeEnd) {
        const q = Math.floor(cur.getMonth() / 3) + 1;
        out.push({ date: new Date(cur), label: `Q${q} '${String(cur.getFullYear()).slice(-2)}` });
        cur.setMonth(cur.getMonth() + 3);
      }
    }
    return out;
  }, [rangeStart, rangeEnd, band]);

  // Position everything in the chart area by percentage so it scales to
  // whatever width the chart column actually gets (was hard-coded to 900px
  // before, which overflowed when the page was narrower).
  const xPctForDate = (date) => {
    if (!date) return 0;
    const offset = daysBetween(rangeStart, date);
    return (offset / totalDays) * 100;
  };
  const cssPct = (date) => `${xPctForDate(date)}%`;

  // Ticks for grid LINES — only those strictly inside the chart cell. The
  // first quarter/month tick often lands just BEFORE rangeStart (e.g. Q2 '26
  // starts April 1 but the range starts May 1), which would render the line
  // at a negative position outside the cell. table-cell overflow:hidden is
  // unreliable across browsers so we just don't draw the line.
  const linesTicks = useMemo(
    () => ticks.filter(tk => {
      const p = xPctForDate(tk.date);
      return p > 0.5 && p < 99.5;
    }),
    [ticks, rangeStart, totalDays]
  );

  // Ticks for LABELS — same idea but slightly more permissive (allow labels
  // very close to the left edge so the chart's starting period is visible).
  const labelTicks = useMemo(
    () => ticks.filter(tk => {
      const p = xPctForDate(tk.date);
      return p >= 0 && p < 99;
    }),
    [ticks, rangeStart, totalDays]
  );

  // Trigger browser print once the layout settles. Use a small delay so fonts /
  // layout finish before the print dialog snapshots.
  useEffect(() => {
    const handle = setTimeout(() => {
      try { window.print(); } catch (e) { /* ignore */ }
    }, 400);
    return () => clearTimeout(handle);
  }, []);

  // Set the document title while the print view is mounted so the browser's
  // "Save as PDF" dialog defaults the file name to "<project> - <range>"
  // instead of the app title. Restore on unmount.
  useEffect(() => {
    const prev = document.title;
    const safeProject = (projectName || 'Schedule').replace(/[\\/:*?"<>|]/g, ' ').trim();
    const safeRange = (rangeLabel || '').replace(/[\\/:*?"<>|]/g, ' ').trim();
    document.title = safeRange ? `${safeProject} - ${safeRange}` : safeProject;
    return () => { document.title = prev; };
  }, [projectName, rangeLabel]);

  const rowHeight = 22;
  const sectionHeight = 26;
  const colHeaderHeight = 28;

  const styles = {
    overlay: {
      position: 'fixed', inset: 0, backgroundColor: '#f1f5f9',
      zIndex: 2000, overflow: 'auto', padding: '20px',
    },
    page: {
      backgroundColor: '#fff', maxWidth: '1180px', margin: '0 auto',
      boxShadow: '0 4px 20px rgba(0,0,0,0.1)', padding: '24px',
      fontFamily: 'system-ui, -apple-system, sans-serif',
    },
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', borderBottom: '2px solid #1e293b', paddingBottom: '8px', marginBottom: '12px' },
    title: { fontSize: '21.5px', fontWeight: 700, color: '#0f172a', margin: 0 },
    subtitle: { fontSize: '13.5px', color: '#475569', marginTop: '4px' },
    rangeBadge: { fontSize: '14.5px', fontWeight: 600, color: '#1e293b' },
    legend: { display: 'flex', gap: '14px', fontSize: '12.5px', color: '#475569', marginBottom: '8px' },
    legendItem: { display: 'flex', alignItems: 'center', gap: '4px' },
    legendDot: { width: '10px', height: '10px', borderRadius: '2px' },
    thCell: {
      height: colHeaderHeight, padding: '0 8px', textAlign: 'left',
      fontSize: '11.5px', fontWeight: 700, color: '#475569', textTransform: 'uppercase',
      backgroundColor: '#f8fafc', borderBottom: '1px solid #cbd5e1',
    },
    tdCell: {
      height: rowHeight, padding: '0 8px', fontSize: '12.5px', color: '#0f172a',
      borderBottom: '1px solid #e2e8f0', boxSizing: 'border-box', verticalAlign: 'middle',
    },
    sectionCell: {
      height: sectionHeight, padding: '0 8px', fontSize: '12.5px', fontWeight: 700,
      textTransform: 'uppercase', color: '#1e293b', backgroundColor: '#e2e8f0',
      borderBottom: '1px solid #cbd5e1', borderTop: '1px solid #cbd5e1',
      boxSizing: 'border-box', verticalAlign: 'middle',
    },
    tick: { position: 'absolute', top: 0, bottom: 0, borderLeft: '1px dotted #cbd5e1' },
    tickLabel: { position: 'absolute', top: 6, fontSize: '11.5px', color: '#475569', transform: 'translateX(2px)' },
    bar: { position: 'absolute', height: rowHeight - 8, top: 4, borderRadius: '2px' },
    progressOverlay: { position: 'absolute', height: '100%', top: 0, left: 0, backgroundColor: 'rgba(0,0,0,0.18)' },
  };

  // Portal the whole overlay to <body> so the @media print rule can
  // `display: none` the React root (#root) cleanly without taking the
  // overlay down with it.
  return ReactDOM.createPortal(
    <>
      <style>{`
        @media print {
          /* @page margin-boxes replace the browser's default header/footer.
             Top-left shows the section that this page is showing (so when a
             section spans multiple pages, every continuation page is labelled).
             Bottom-left holds the Job #, bottom-right holds page X of Y. */
          .section-row { string-set: current-section attr(data-section); }
          @page {
            size: landscape;
            margin: 0.55in 0.5in 0.6in 0.5in;
            @top-left {
              content: string(current-section);
              font-family: system-ui, -apple-system, sans-serif;
              font-size: 9pt;
              font-weight: 700;
              color: #1e293b;
              text-transform: uppercase;
              letter-spacing: 0.04em;
            }
            @bottom-left {
              content: "${projectNumber ? ('Job # ' + projectNumber).replace(/"/g, '\\"') : ''}";
              font-family: system-ui, -apple-system, sans-serif;
              font-size: 9pt;
              color: #475569;
            }
            @bottom-right {
              content: "Page " counter(page) " of " counter(pages);
              font-family: system-ui, -apple-system, sans-serif;
              font-size: 9pt;
              color: #475569;
            }
          }
          body { background: #fff !important; }
          /* Take the React app entirely out of the printed flow so it
             doesn't reserve any page space. The print overlay is portalled
             outside of #root specifically so this works. */
          #root { display: none !important; }
          .print-overlay {
            position: static !important;
            padding: 0 !important; margin: 0 !important;
            overflow: visible !important; background: #fff !important;
          }
          /* Force backgrounds + colors to actually print. Without this,
             browsers default to stripping background colors to save ink —
             which turns the Gantt bars and section bands invisible, so
             they appear as white blocks masking the dotted grid lines. */
          .print-overlay, .print-overlay * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            color-adjust: exact !important;
          }
          .print-page {
            box-shadow: none !important;
            padding: 0 !important; margin: 0 !important;
            max-width: 100% !important;
          }
          .no-print { display: none !important; }
        }
      `}</style>
      <div className="print-overlay" style={styles.overlay}>
        <div className="no-print" style={{ maxWidth: '1180px', margin: '0 auto 12px auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontSize: '14.5px', color: '#475569' }}>
            Tip: pick "Save as PDF" for a digital copy. If you see the page URL at the bottom, turn off "Headers and footers" in the print dialog — the Job # is added through CSS instead.
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={() => window.print()} style={{ background: '#3b82f6', color: '#fff', border: 'none', borderRadius: '6px', padding: '8px 16px', fontSize: '14.5px', fontWeight: 600, cursor: 'pointer' }}>
              Print
            </button>
            <button onClick={onClose} style={{ background: '#fff', color: '#1e293b', border: '1px solid #cbd5e1', borderRadius: '6px', padding: '8px 16px', fontSize: '14.5px', fontWeight: 500, cursor: 'pointer' }}>
              Close
            </button>
          </div>
        </div>

        <div ref={printRef} className="print-page" style={styles.page}>
          <div style={styles.header}>
            <div>
              <h1 style={styles.title}>{projectName || 'Project Schedule'}</h1>
              <div style={styles.subtitle}>
                Schedule range: {fmtRangeHeader(from)} – {fmtRangeHeader(to)}
                <span style={{ marginLeft: '12px' }}>Printed {new Date().toLocaleDateString()}</span>
              </div>
            </div>
            <div style={styles.rangeBadge}>{rangeLabel}</div>
          </div>

          <div style={styles.legend}>
            <div style={styles.legendItem}><div style={{ ...styles.legendDot, backgroundColor: STATUS_COLORS['not started'] }} />Not Started</div>
            <div style={styles.legendItem}><div style={{ ...styles.legendDot, backgroundColor: STATUS_COLORS['in-progress'] }} />In Progress</div>
            <div style={styles.legendItem}><div style={{ ...styles.legendDot, backgroundColor: STATUS_COLORS.complete }} />Complete</div>
          </div>

          {/* Real HTML table so the browser repeats <thead> on every printed
              page, and we can apply per-section page-break controls on each
              section row. */}
          <table className="print-schedule-table" style={{
            width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed',
            border: '1px solid #cbd5e1',
          }}>
            <colgroup>
              <col style={{ width: '60px' }} />
              <col style={{ width: '170px' }} />
              <col style={{ width: '110px' }} />
              <col />
            </colgroup>
            <thead>
              {/* Time-axis row — repeats on every printed page */}
              <tr>
                <th colSpan={3} style={{
                  height: colHeaderHeight, padding: 0, borderBottom: '1px solid #cbd5e1',
                  backgroundColor: '#f8fafc',
                }} />
                <th style={{
                  height: colHeaderHeight, padding: 0, position: 'relative',
                  borderBottom: '1px solid #cbd5e1', borderLeft: '1px solid #cbd5e1',
                  backgroundColor: '#f8fafc', overflow: 'hidden',
                }}>
                  {labelTicks.map((tk, i) => (
                    <div key={`th-${i}`} style={{ ...styles.tickLabel, left: cssPct(tk.date), top: 6 }}>{tk.label}</div>
                  ))}
                </th>
              </tr>
              {/* Column-label row — also repeats */}
              <tr>
                <th style={styles.thCell}>Code</th>
                <th style={styles.thCell}>Task</th>
                <th style={{ ...styles.thCell, textAlign: 'right' }}>Dates</th>
                <th style={{ ...styles.thCell, borderLeft: '1px solid #cbd5e1' }} />
              </tr>
            </thead>
            <tbody>
              {visibleSections.map((section, sIdx) => (
                <React.Fragment key={`sec-${section}`}>
                  <tr
                    className="section-row"
                    data-section={section}
                    style={{
                      // First section starts naturally on page 1; every
                      // subsequent section is forced to start a fresh page.
                      pageBreakBefore: sIdx === 0 ? 'auto' : 'always',
                      breakBefore: sIdx === 0 ? 'auto' : 'always',
                    }}
                  >
                    <td colSpan={3} style={styles.sectionCell}>{section}</td>
                    <td style={{ ...styles.sectionCell, borderLeft: '1px solid #cbd5e1', padding: 0 }} />
                  </tr>
                  {(filteredGrouped[section] || []).map(task => {
                    const s = parseDate(task.start_date);
                    const e = parseDate(task.finish_date) || s;
                    const leftPct = s ? xPctForDate(s < rangeStart ? rangeStart : s) : 0;
                    const rightPct = s ? xPctForDate(e > rangeEnd ? rangeEnd : e) : 0;
                    const widthPct = Math.max(0.5, rightPct - leftPct);
                    const isMilestone = !!task.milestone_flag;
                    // a parent's % is its subtasks', kept to 2 decimals (server/subtasks.js): printed whole
                    const progress = task._kids ? Math.round(Number(task.percent_complete) || 0) : (task.percent_complete || 0);
                    const barColor = getStatusColor(task);
                    return (
                      <tr key={`row-${task.id}`} style={{ pageBreakInside: 'avoid', breakInside: 'avoid' }}>
                        <td style={{ ...styles.tdCell, color: '#475569', fontWeight: 500 }}>{task.task_code}</td>
                        <td style={{ ...styles.tdCell, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', paddingLeft: task._depth ? 18 : undefined, fontWeight: task._kids ? 600 : undefined }} title={task.task_name}>{task.task_name}</td>
                        <td style={{ ...styles.tdCell, textAlign: 'right', color: '#64748b', fontSize: '11.5px', whiteSpace: 'nowrap' }}>
                          {fmtDateShort(task.start_date)}–{fmtDateShort(task.finish_date)}
                        </td>
                        <td style={{ ...styles.tdCell, borderLeft: '1px solid #cbd5e1', padding: 0, position: 'relative', overflow: 'hidden' }}>
                          {linesTicks.map((tk, i) => (
                            <div key={`gt-${task.id}-${i}`} style={{ ...styles.tick, left: cssPct(tk.date) }} />
                          ))}
                          {s && (isMilestone ? (
                            // Clamp the 10px-wide diamond so it never extends
                            // outside the chart cell on either side.
                            <div style={{
                              position: 'absolute',
                              left: `clamp(0px, calc(${leftPct}% - 5px), calc(100% - 10px))`,
                              top: (rowHeight - 10) / 2,
                              width: 0, height: 0,
                              borderLeft: '5px solid transparent', borderRight: '5px solid transparent',
                              // a milestone's own color when it has one (lib/milestoneColor.js), else its status color as before
                              borderBottom: `5px solid ${milestoneColor(task, barColor)}`, borderTop: `5px solid ${milestoneColor(task, barColor)}`,
                            }} />
                          ) : (
                            <div style={{ ...styles.bar, left: `${leftPct}%`, width: `${widthPct}%`, backgroundColor: barColor }}>
                              {progress > 0 && progress < 100 && (
                                <div style={{ position: 'absolute', height: '100%', left: 0, width: `${progress}%`, backgroundColor: 'rgba(0,0,0,0.25)', borderRadius: '2px 0 0 2px' }} />
                              )}
                            </div>
                          ))}
                        </td>
                      </tr>
                    );
                  })}
                </React.Fragment>
              ))}
            </tbody>
          </table>

          <div style={{ marginTop: '8px', fontSize: '11.5px', color: '#94a3b8', textAlign: 'right' }}>
            {visibleSections.reduce((sum, s) => sum + (filteredGrouped[s] || []).filter((x) => !x._kids).length, 0)} tasks shown · Generated by E3 Summit
          </div>
        </div>
      </div>
    </>,
    document.body
  );
};

export default PrintScheduleView;
